import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useLanguage } from "./LanguageContext";

const normalizeApiBase = (url) => String(url || "").trim().replace(/\/+$/, "");

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const apiBases = useMemo(() => (
    [
      process.env.REACT_APP_API_URL_DE || process.env.REACT_APP_API_URL || "",
      process.env.REACT_APP_API_URL_RU || "https://api-ru.swingmentor.pro",
    ]
      .map(normalizeApiBase)
      .filter((base, index, allBases) => base && allBases.indexOf(base) === index)
  ), []);

  const requestCode = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      setError(t("passwordReset.emailRequired"));
      return;
    }

    setLoading(true);
    try {
      const responses = await Promise.allSettled(
        apiBases.map((apiBase) => fetch(`${apiBase}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail, lang }),
        }))
      );
      if (!responses.some((result) => result.status === "fulfilled" && result.value.ok)) {
        throw new Error(t("passwordReset.requestFailed"));
      }
      setStep("reset");
      setNotice(t("passwordReset.codeSent"));
    } catch (requestError) {
      setError(requestError.message || t("passwordReset.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!/^\d{6}$/.test(code)) {
      setError(t("passwordReset.invalidCode"));
      return;
    }
    if (newPassword.length < 10) {
      setError(t("passwordReset.passwordLength"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("passwordReset.passwordMismatch"));
      return;
    }

    setLoading(true);
    try {
      let succeeded = false;
      for (const apiBase of apiBases) {
        try {
          const response = await fetch(`${apiBase}/api/auth/reset-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: email.trim().toLowerCase(),
              code,
              new_password: newPassword,
            }),
          });
          if (response.ok) {
            succeeded = true;
            break;
          }
        } catch {
          // Try the other data region.
        }
      }
      if (!succeeded) throw new Error(t("passwordReset.invalidCode"));
      navigate("/log-in", {
        replace: true,
        state: { passwordReset: true },
      });
    } catch (resetError) {
      setError(resetError.message || t("passwordReset.invalidCode"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Gradient />
      <Card>
        <Logo>SWING<Green>MENTOR</Green></Logo>
        <Tagline>{t("passwordReset.title")}</Tagline>
        <Description>
          {t(step === "request"
            ? "passwordReset.requestDescription"
            : "passwordReset.resetDescription")}
        </Description>

        <Form onSubmit={step === "request" ? requestCode : resetPassword}>
          <Field>
            <Label>{t("login.email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              disabled={step === "reset"}
              autoFocus
            />
          </Field>

          {step === "reset" && (
            <>
              <Field>
                <Label>{t("passwordReset.code")}</Label>
                <CodeInput
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoComplete="one-time-code"
                />
              </Field>
              <Field>
                <Label>{t("passwordReset.newPassword")}</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              <Field>
                <Label>{t("passwordReset.confirmPassword")}</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </>
          )}

          {notice && <Notice>{notice}</Notice>}
          {error && <ErrorBox>{error}</ErrorBox>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading
              ? t("passwordReset.loading")
              : t(step === "request"
                ? "passwordReset.sendCode"
                : "passwordReset.submit")}
          </SubmitBtn>
        </Form>

        <FooterLink to="/log-in">{t("passwordReset.back")}</FooterLink>
      </Card>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100svh;
  background: #f5faf5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow: hidden;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(76,175,80,0.1) 0%, transparent 70%);
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);

  @media (max-width: 480px) { padding: 2rem 1.25rem; }
`;

const Logo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #111;
`;

const Green = styled.span`color: #4CAF50;`;

const Tagline = styled.div`
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: rgba(0,0,0,0.45);
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Description = styled.p`
  margin: 1.5rem 0;
  color: rgba(0,0,0,0.6);
  font-size: 0.88rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.45);
`;

const Input = styled.input`
  background: #f8f8f8;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: #111;
  outline: none;

  &:focus { border-color: #4CAF50; background: #fff; }
  &:disabled { color: rgba(0,0,0,0.5); }
`;

const CodeInput = styled(Input)`
  font-size: 1.25rem;
  letter-spacing: 0.45em;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const ErrorBox = styled.div`
  background: rgba(220,50,50,0.1);
  border: 1px solid rgba(220,50,50,0.3);
  border-radius: 6px;
  padding: 0.7rem 1rem;
  color: #c62828;
  font-size: 0.82rem;
`;

const Notice = styled(ErrorBox)`
  background: rgba(76,175,80,0.1);
  border-color: rgba(76,175,80,0.3);
  color: #2e7d32;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 0.9rem;
  background: #4CAF50;
  border: 2px solid #4CAF50;
  border-radius: 6px;
  color: #000;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: #388E3C; color: #fff; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const FooterLink = styled(Link)`
  display: block;
  margin-top: 1.5rem;
  color: #4CAF50;
  text-decoration: none;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
`;

export default ForgotPassword;
